import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/types";
import { z } from "zod";

export type FeeSchedulesAccessJSON = z.infer<typeof FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ>;

export type FeeSchedulesAccessParams = z.infer<typeof FeeSchedulesAccessSchema.FeeSchedulesAccessZ>;

export default class FeeSchedulesAccessSchema {
  public static readonly FeeSchedulesAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.FeeSchedulesAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly FeeSchedulesAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.FeeSchedulesAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): FeeSchedulesAccessParams {
    return parseOrThrow(FeeSchedulesAccessSchema.FeeSchedulesAccessZ, values, {
      logError: true,
      schemaName: "FeeSchedulesAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): FeeSchedulesAccessJSON {
    return parseOrThrow(FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ, values, {
      logError: true,
      schemaName: "FeeSchedulesAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is FeeSchedulesAccessParams {
    try {
      FeeSchedulesAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is FeeSchedulesAccessJSON {
    try {
      FeeSchedulesAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: FeeSchedulesAccessJSON): FeeSchedulesAccessParams {
    const parsed = parseOrThrow(FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ, data, {
      logError: true,
      schemaName: "FeeSchedulesAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return FeeSchedulesAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: FeeSchedulesAccessParams): FeeSchedulesAccessJSON {
    const parsed = FeeSchedulesAccessSchema.validate(notification);
    return FeeSchedulesAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ,
    FeeSchedulesAccessSchema.FeeSchedulesAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.FeeSchedulesAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.FeeSchedulesAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
